import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Plus, 
  Trash2, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';

const AddEditPanditModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  pandit = null, 
  services = [],
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    profile: {
      name: '',
      bio: '',
      city: '',
      state: '',
      country: 'India',
      languages: [],
      experience: 0,
      image: ''
    },
    expertise: [],
    isVerified: false,
    featuredPandit: false
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [newLanguage, setNewLanguage] = useState('');
  const [newExpertise, setNewExpertise] = useState({
    serviceId: '',
    experienceLevel: 'Beginner',
    customTitle: ''
  });

  const isEditMode = !!pandit;

  const experienceLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  useEffect(() => {
    if (pandit) {
      setFormData({
        email: pandit.email || '',
        phone: pandit.phone || '',
        password: '', // Don't pre-fill password in edit mode
        profile: {
          name: pandit.name || pandit.profile?.name || '',
          bio: pandit.profile?.bio || '',
          city: pandit.location || pandit.profile?.city || '',
          state: pandit.state || pandit.profile?.state || '', // Handle both root level and profile.state
          country: pandit.profile?.country || 'India',
          languages: pandit.languages || pandit.profile?.languages || [], // Handle both root level and profile.languages
          experience: pandit.experience || pandit.profile?.experience || 0,
          image: pandit.profile?.image || ''
        },
        expertise: pandit.expertise?.map(exp => ({
          serviceId: exp.serviceDetails?.id || '',
          experienceLevel: exp.experienceLevel || 'Beginner',
          customTitle: exp.customTitle || '',
          serviceDetails: exp.serviceDetails || null
        })) || [],
        isVerified: pandit.isVerified || false,
        featuredPandit: pandit.featuredPandit || false
      });
    } else {
      setFormData({
        email: '',
        phone: '',
        password: '',
        profile: {
          name: '',
          bio: '',
          city: '',
          state: '',
          country: 'India',
          languages: [],
          experience: 0,
          image: ''
        },
        expertise: [],
        isVerified: false,
        featuredPandit: false
      });
    }
    setErrors({});
  }, [pandit, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }

    if (!isEditMode && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (!isEditMode && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.profile.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.profile.city.trim()) {
      newErrors.city = 'City is required';
    }

    // Make state optional in edit mode if not provided by API
    if (!isEditMode && !formData.profile.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Valid 10-digit phone number required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      profile: {
        ...formData.profile,
        experience: parseInt(formData.profile.experience) || 0
      }
    };

    // Don't send empty password in edit mode
    if (isEditMode && !submitData.password.trim()) {
      delete submitData.password;
    }

    onSave(submitData);
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field] || errors[field.split('.')[1]]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
        [field.split('.')[1]]: ''
      }));
    }
  };

  const addLanguage = () => {
    if (newLanguage.trim() && !formData.profile.languages.includes(newLanguage.trim())) {
      handleInputChange('profile.languages', [...formData.profile.languages, newLanguage.trim()]);
      setNewLanguage('');
    }
  };

  const removeLanguage = (index) => {
    const updatedLanguages = formData.profile.languages.filter((_, i) => i !== index);
    handleInputChange('profile.languages', updatedLanguages);
  };

  const addExpertise = () => {
    if (newExpertise.serviceId && !formData.expertise.find(exp => exp.serviceId === newExpertise.serviceId)) {
      handleInputChange('expertise', [...formData.expertise, { ...newExpertise }]);
      setNewExpertise({ serviceId: '', experienceLevel: 'Beginner', customTitle: '' });
    }
  };

  const removeExpertise = (index) => {
    const updatedExpertise = formData.expertise.filter((_, i) => i !== index);
    handleInputChange('expertise', updatedExpertise);
  };

  if (!isOpen) return null;

  return (
    <div translate="no" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Pandit' : 'Add New Pandit'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="pandit@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="9876543210"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              {!isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`pr-10 pl-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.profile.name}
                    onChange={(e) => handleInputChange('profile.name', e.target.value)}
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Pandit Name"
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.profile.city}
                    onChange={(e) => handleInputChange('profile.city', e.target.value)}
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.city ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="City"
                  />
                </div>
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <select
                  value={formData.profile.state}
                  onChange={(e) => handleInputChange('profile.state', e.target.value)}
                  className={`px-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select State</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience (Years)
                </label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.profile.experience}
                  onChange={(e) => handleInputChange('profile.experience', e.target.value)}
                  className="px-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              <textarea
                value={formData.profile.bio}
                onChange={(e) => handleInputChange('profile.bio', e.target.value)}
                rows={3}
                className="px-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Brief description about the pandit..."
              />
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Languages
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Add language (e.g., Hindi, English)"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                />
                <button
                  type="button"
                  onClick={addLanguage}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.profile.languages.map((language, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
                  >
                    {language}
                    <button
                      type="button"
                      onClick={() => removeLanguage(index)}
                      className="ml-2 text-orange-600 hover:text-orange-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Service Expertise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Expertise
              </label>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                <select
                  value={newExpertise.serviceId}
                  onChange={(e) => setNewExpertise(prev => ({ ...prev, serviceId: e.target.value }))}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.title}</option>
                  ))}
                </select>
                <select
                  value={newExpertise.experienceLevel}
                  onChange={(e) => setNewExpertise(prev => ({ ...prev, experienceLevel: e.target.value }))}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  {experienceLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newExpertise.customTitle}
                  onChange={(e) => setNewExpertise(prev => ({ ...prev, customTitle: e.target.value }))}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Custom title (optional)"
                />
                <button
                  type="button"
                  onClick={addExpertise}
                  className="px-4 py-2 flex items-center justify-center gap-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
                >
                  Add Expertise
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                {formData.expertise.map((exp, index) => {
                  const service = services.find(s => s.id === exp.serviceId);
                  const serviceTitle = exp.serviceDetails?.title || service?.title || 'Unknown Service';
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium">{serviceTitle}</span>
                        <span className="text-gray-500 ml-2">({exp.experienceLevel})</span>
                        {exp.customTitle && (
                          <span className="text-orange-600 ml-2">- {exp.customTitle}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExpertise(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Status Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isVerified"
                  checked={formData.isVerified}
                  onChange={(e) => handleInputChange('isVerified', e.target.checked)}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="isVerified" className="ml-2 text-sm font-medium text-gray-700">
                  Verified Pandit
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featuredPandit"
                  checked={formData.featuredPandit}
                  onChange={(e) => handleInputChange('featuredPandit', e.target.checked)}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="featuredPandit" className="ml-2 text-sm font-medium text-gray-700">
                  Featured Pandit
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {loading ? 'Saving...' : (isEditMode ? 'Update Pandit' : 'Create Pandit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditPanditModal;
